import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  RefreshCw, Trash2, Pause, Play, Plus, Rss,
  CheckCircle, AlertCircle, PauseCircle, Pencil, Search,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  getFeeds, createFeed, deleteFeed, syncFeed, updateFeed, extractFeedFields,
} from '../../utils/api';
import type {
  FeedWithSchedulerStatus, FeedFormat, FeedType, FeedStatus, FieldMapping,
} from '../../utils/types';
import { useAdminLang } from '../AdminLangContext';

interface AdminContext { secret: string }

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const FORMAT_LABELS: Record<FeedFormat, string> = {
  json: 'JSON', xml: 'XML', csv: 'CSV',
  'google-merchant': 'Google Merchant', skroutz: 'Skroutz',
  bestprice: 'BestPrice', crawl: 'Web Crawl',
};

const TYPE_LABELS: Record<FeedType, string> = {
  products: 'Products', categories: 'Categories',
  articles: 'Articles', brands: 'Brands',
};

const SCHEDULE_OPTIONS: { value: string; label: string; cron?: string }[] = [
  { value: 'manual', label: 'Manual' },
  { value: 'hourly', label: 'Hourly', cron: '0 * * * *' },
  { value: 'daily', label: 'Daily', cron: '0 3 * * *' },
];

const FORMAT_PRESETS: Record<FeedFormat, Partial<FieldMapping>> = {
  'google-merchant': {
    id: 'g:id', name: 'g:title', price: 'g:price',
    description: 'g:description', imageUrl: 'g:image_link',
    url: 'g:link', inStock: 'g:availability', category: 'g:product_type',
    itemPath: 'rss/channel/item',
  },
  skroutz: {
    id: 'UniqueID', name: 'Name', price: 'Price',
    description: 'Description', imageUrl: 'ImageURL',
    url: 'ProductURL', inStock: 'Availability', category: 'Category',
    itemPath: 'products/product',
  },
  bestprice: {
    id: 'productId', name: 'name', price: 'price_with_vat',
    description: 'description', imageUrl: 'image',
    url: 'link', inStock: 'availability', category: 'category_path',
    itemPath: 'store/products/product',
  },
  json: { id: 'id', name: 'name', price: 'price' },
  xml: { id: 'id', name: 'name', price: 'price', itemPath: 'products/product' },
  csv: { id: 'id', name: 'name', price: 'price' },
  crawl: { id: '', name: '', price: '' },
};

/** Mapping field definitions per feed type */
interface MappingFieldDef {
  key: string;
  label: string;
  required?: boolean;
  placeholder?: string;
}

const PRODUCT_FIELDS: MappingFieldDef[] = [
  { key: 'id', label: 'ID', required: true, placeholder: 'id' },
  { key: 'name', label: 'Name', required: true, placeholder: 'name' },
  { key: 'price', label: 'Price', required: true, placeholder: 'price' },
  { key: 'category', label: 'Category', placeholder: 'category' },
  { key: 'description', label: 'Description', placeholder: 'description' },
  { key: 'imageUrl', label: 'Image URL', placeholder: 'imageUrl' },
  { key: 'url', label: 'Product URL', placeholder: 'url' },
  { key: 'inStock', label: 'In Stock', placeholder: 'inStock' },
  { key: 'salePrice', label: 'Sale Price', placeholder: 'salePrice' },
  { key: 'producer', label: 'Producer', placeholder: 'producer' },
  { key: 'region', label: 'Region', placeholder: 'region' },
  { key: 'country', label: 'Country', placeholder: 'country' },
  { key: 'grape', label: 'Grape', placeholder: 'grape' },
  { key: 'vintage', label: 'Vintage', placeholder: 'vintage' },
  { key: 'type', label: 'Wine Type', placeholder: 'type' },
  { key: 'rating', label: 'Rating', placeholder: 'rating' },
  { key: 'tags', label: 'Tags', placeholder: 'tags' },
  { key: 'nameEl', label: 'Name (Greek)', placeholder: 'nameEl' },
];

const ARTICLE_FIELDS: MappingFieldDef[] = [
  { key: 'id', label: 'ID', required: true, placeholder: 'id' },
  { key: 'title', label: 'Title', required: true, placeholder: 'title' },
  { key: 'url', label: 'URL', required: true, placeholder: 'url' },
  { key: 'excerpt', label: 'Excerpt', placeholder: 'shortDescription' },
  { key: 'content', label: 'Content', placeholder: 'fullDescription' },
  { key: 'category', label: 'Category', placeholder: 'category' },
  { key: 'imageUrl', label: 'Image URL', placeholder: 'imageUrl' },
  { key: 'publishedAt', label: 'Published At', placeholder: 'publishedAt' },
];

const CATEGORY_FIELDS: MappingFieldDef[] = [
  { key: 'id', label: 'ID', required: true, placeholder: 'id' },
  { key: 'name', label: 'Name', required: true, placeholder: 'name' },
  { key: 'description', label: 'Description', placeholder: 'description' },
  { key: 'imageUrl', label: 'Image URL', placeholder: 'imageUrl' },
  { key: 'url', label: 'URL', placeholder: 'url' },
];

const BRAND_FIELDS: MappingFieldDef[] = [
  { key: 'id', label: 'ID', required: true, placeholder: 'id' },
  { key: 'name', label: 'Name', required: true, placeholder: 'name' },
  { key: 'description', label: 'Description', placeholder: 'description' },
  { key: 'imageUrl', label: 'Image URL', placeholder: 'imageUrl' },
  { key: 'url', label: 'URL', placeholder: 'url' },
];

const FEED_TYPE_FIELDS: Record<FeedType, MappingFieldDef[]> = {
  products: PRODUCT_FIELDS,
  articles: ARTICLE_FIELDS,
  categories: CATEGORY_FIELDS,
  brands: BRAND_FIELDS,
};

const STRUCTURE_FIELDS: MappingFieldDef[] = [
  { key: 'rootPath', label: 'Root Path (JSON)', placeholder: 'data.products' },
  { key: 'itemPath', label: 'Item Path (XML)', placeholder: 'products/product' },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function cronToScheduleValue(cron?: string): string {
  if (!cron) return 'manual';
  return SCHEDULE_OPTIONS.find((o) => o.cron === cron)?.value ?? 'manual';
}

function scheduleValueToCron(value: string): string | undefined {
  return SCHEDULE_OPTIONS.find((o) => o.value === value)?.cron;
}

function scheduleLabel(cron?: string): string {
  if (!cron) return 'Manual';
  return SCHEDULE_OPTIONS.find((o) => o.cron === cron)?.label ?? cron;
}

const STATUS_BADGE: Record<FeedStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' }> = {
  active: { label: 'Active', variant: 'default' },
  paused: { label: 'Paused', variant: 'secondary' },
  error: { label: 'Error', variant: 'destructive' },
};

function formatDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('el-GR', { dateStyle: 'short', timeStyle: 'short' });
}

function defaultMappingForType(type: FeedType): Record<string, string> {
  const fields = FEED_TYPE_FIELDS[type];
  const m: Record<string, string> = {};
  for (const f of fields) m[f.key] = f.placeholder ?? '';
  return m;
}

function feedMappingToRecord(mapping?: FieldMapping): Record<string, string> {
  if (!mapping) return {};
  const m: Record<string, string> = {};
  for (const [k, v] of Object.entries(mapping)) {
    if (typeof v === 'string') m[k] = v;
  }
  return m;
}

function recordToFieldMapping(rec: Record<string, string>): FieldMapping {
  const cleaned: Record<string, string> = {};
  for (const [k, v] of Object.entries(rec)) {
    if (v.trim()) cleaned[k] = v.trim();
  }
  return {
    id: cleaned.id ?? '',
    name: cleaned.name ?? cleaned.title ?? '',
    price: cleaned.price ?? '0',
    ...cleaned,
  } as FieldMapping;
}

/* ------------------------------------------------------------------ */
/*  Form type                                                          */
/* ------------------------------------------------------------------ */

interface PaginationConfig {
  enabled: boolean;
  pageParam: string;
  pageSizeParam: string;
  pageSize: number;
  hasNextPagePath: string;
  totalPagesPath: string;
  maxPages: number;
}

const EMPTY_PAGINATION: PaginationConfig = {
  enabled: false,
  pageParam: 'pageNumber',
  pageSizeParam: 'pageSize',
  pageSize: 100,
  hasNextPagePath: 'hasNextPage',
  totalPagesPath: '',
  maxPages: 100,
};

interface NewFeedForm {
  name: string;
  url: string;
  format: FeedFormat;
  type: FeedType;
  schedule: string;
  lang: string;
  mapping: Record<string, string>;
  pagination: PaginationConfig;
}

const EMPTY_FORM: NewFeedForm = {
  name: '', url: '', format: 'json', type: 'products', schedule: 'manual', lang: 'el',
  mapping: defaultMappingForType('products'),
  pagination: { ...EMPTY_PAGINATION },
};

const LANG_OPTIONS = [
  { value: 'el', label: 'Greek (el)' },
  { value: 'en', label: 'English (en)' },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function AdminFeedsPage() {
  const { secret } = useOutletContext<AdminContext>();
  const { lang } = useAdminLang();

  const [feeds, setFeeds] = useState<FeedWithSchedulerStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingFeedId, setEditingFeedId] = useState<string | null>(null);
  const [form, setForm] = useState<NewFeedForm>(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Field detection
  const [detectedFields, setDetectedFields] = useState<string[]>([]);
  const [isDetecting, setIsDetecting] = useState(false);

  const loadFeeds = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getFeeds(secret, lang);
      setFeeds(result.feeds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feeds');
    } finally {
      setLoading(false);
    }
  }, [secret, lang]);

  useEffect(() => { loadFeeds(); }, [loadFeeds]);

  /* ---- Actions ---- */

  const handleSync = async (id: string) => {
    setSyncingId(id);
    try {
      await syncFeed(secret, id);
      await loadFeeds();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncingId(null);
    }
  };

  const handleTogglePause = async (feed: FeedWithSchedulerStatus) => {
    const newStatus: FeedStatus = feed.status === 'paused' ? 'active' : 'paused';
    try {
      await updateFeed(secret, feed.id, { status: newStatus });
      await loadFeeds();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this feed? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await deleteFeed(secret, id);
      await loadFeeds();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  /* ---- Dialog open/close ---- */

  const handleOpenCreate = () => {
    setDialogMode('create');
    setEditingFeedId(null);
    setForm({ ...EMPTY_FORM, lang });
    setDetectedFields([]);
    setFormError(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (feed: FeedWithSchedulerStatus) => {
    setDialogMode('edit');
    setEditingFeedId(feed.id);
    const feedPagination = (feed as any).pagination;
    setForm({
      name: feed.name ?? '',
      url: feed.url,
      format: feed.format,
      type: feed.type ?? 'products',
      schedule: cronToScheduleValue(feed.schedule),
      lang: feed.lang ?? 'el',
      mapping: feedMappingToRecord(feed.mapping),
      pagination: feedPagination
        ? { ...EMPTY_PAGINATION, ...feedPagination }
        : { ...EMPTY_PAGINATION },
    });
    setDetectedFields([]);
    setFormError(null);
    setDialogOpen(true);
  };

  /* ---- Format / Type change handlers ---- */

  const handleFormatChange = (fmt: FeedFormat) => {
    setForm((f) => {
      const next = { ...f, format: fmt };
      // Apply preset only when creating
      if (dialogMode === 'create') {
        const preset = FORMAT_PRESETS[fmt] ?? {};
        next.mapping = { ...f.mapping, ...feedMappingToRecord(preset as FieldMapping) };
      }
      return next;
    });
  };

  const handleTypeChange = (type: FeedType) => {
    setForm((f) => {
      const next = { ...f, type };
      // Reset mapping to defaults for the new type, preserving rootPath/itemPath
      if (dialogMode === 'create') {
        const newMapping = defaultMappingForType(type);
        if (f.mapping.rootPath) newMapping.rootPath = f.mapping.rootPath;
        if (f.mapping.itemPath) newMapping.itemPath = f.mapping.itemPath;
        next.mapping = newMapping;
      }
      return next;
    });
  };

  /* ---- Field detection ---- */

  const handleDetectFields = async () => {
    if (!form.url.trim()) { setFormError('Enter a URL first'); return; }
    setIsDetecting(true);
    setFormError(null);
    try {
      const result = await extractFeedFields(secret, form.url, form.format);
      setDetectedFields(result.fields);
      if (result.format && result.format !== form.format) {
        setForm((f) => ({ ...f, format: result.format as FeedFormat }));
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to detect fields');
    } finally {
      setIsDetecting(false);
    }
  };

  /* ---- Submit ---- */

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.url.trim()) {
      setFormError('Name and URL are required');
      return;
    }
    setCreating(true);
    setFormError(null);
    try {
      const mapping = recordToFieldMapping(form.mapping);
      const pagination = form.pagination.enabled ? form.pagination : undefined;
      if (dialogMode === 'edit' && editingFeedId) {
        await updateFeed(secret, editingFeedId, {
          name: form.name.trim(),
          url: form.url.trim(),
          format: form.format,
          type: form.type,
          schedule: scheduleValueToCron(form.schedule),
          mapping,
          lang: form.lang,
          pagination,
        });
      } else {
        await createFeed(secret, {
          name: form.name.trim(),
          url: form.url.trim(),
          format: form.format,
          type: form.type,
          schedule: scheduleValueToCron(form.schedule),
          mapping,
          status: 'active',
          lang: form.lang,
          pagination,
        });
      }
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      setEditingFeedId(null);
      await loadFeeds();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : dialogMode === 'edit' ? 'Failed to update feed' : 'Failed to create feed');
    } finally {
      setCreating(false);
    }
  };

  /* ---- Mapping field update helper ---- */

  const setMappingField = (key: string, value: string) => {
    setForm((f) => ({ ...f, mapping: { ...f.mapping, [key]: value } }));
  };

  /* ---- Derived ---- */

  const activeCount = feeds.filter((f) => f.status === 'active').length;
  const errorCount = feeds.filter((f) => f.status === 'error').length;
  const pausedCount = feeds.filter((f) => f.status === 'paused').length;
  const typeFields = FEED_TYPE_FIELDS[form.type] ?? PRODUCT_FIELDS;
  const requiredFields = typeFields.filter((f) => f.required);
  const optionalFields = typeFields.filter((f) => !f.required);
  const showStructureFields = ['json', 'xml', 'csv'].includes(form.format);
  const datalistId = 'detected-fields-list';

  /* ---- Render ---- */

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Feed Management</h1>
          <p className="text-muted-foreground">Manage data feeds that power your search engine</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadFeeds} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={handleOpenCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Feed
          </Button>
        </div>
      </div>

      {/* Status summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="text-3xl font-bold">{activeCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <div>
              <p className="text-sm text-muted-foreground">Error</p>
              <p className="text-3xl font-bold">{errorCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <PauseCircle className="h-8 w-8 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Paused</p>
              <p className="text-3xl font-bold">{pausedCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Feeds table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Rss className="h-5 w-5" />
            Feeds ({feeds.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 text-center text-muted-foreground">Loading feeds...</div>
          ) : feeds.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              No feeds configured yet. Add one to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Lang</TableHead>
                  <TableHead>Format</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Synced</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feeds.map((feed) => {
                  const status: FeedStatus = (feed.status as FeedStatus) ?? 'active';
                  const badgeMeta = STATUS_BADGE[status] ?? STATUS_BADGE.active;
                  return (
                    <TableRow key={feed.id}>
                      <TableCell>
                        <div className="font-medium">{feed.name ?? feed.id}</div>
                        <div className="max-w-xs truncate text-xs text-muted-foreground">{feed.url}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{TYPE_LABELS[feed.type ?? 'products']}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{feed.lang ?? 'el'}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{FORMAT_LABELS[feed.format] ?? feed.format}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={badgeMeta.variant}>{badgeMeta.label}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(feed.lastSyncAt)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {scheduleLabel(feed.schedule)}
                        {feed.schedulerStatus?.nextRun && (
                          <div className="text-xs">
                            Next: {formatDate(feed.schedulerStatus.nextRun)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" title="Sync now"
                            disabled={syncingId === feed.id} onClick={() => handleSync(feed.id)}>
                            <RefreshCw className={`h-4 w-4 ${syncingId === feed.id ? 'animate-spin' : ''}`} />
                          </Button>
                          <Button variant="ghost" size="icon"
                            title={status === 'paused' ? 'Resume' : 'Pause'}
                            onClick={() => handleTogglePause(feed)}>
                            {status === 'paused' ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                          </Button>
                          <Button variant="ghost" size="icon" title="Edit"
                            onClick={() => handleOpenEdit(feed)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Delete"
                            disabled={deletingId === feed.id} onClick={() => handleDelete(feed.id)}
                            className="text-destructive hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit Feed Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialogMode === 'edit' ? 'Edit Feed' : 'Add New Feed'}</DialogTitle>
            <DialogDescription>
              {dialogMode === 'edit'
                ? 'Update the feed configuration and field mapping.'
                : 'Configure a new data feed and map its fields.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {/* ---- Basic info ---- */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="feed-name">Name</Label>
                <Input id="feed-name" placeholder="My Product Feed"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="feed-schedule">Schedule</Label>
                <Select value={form.schedule}
                  onValueChange={(v) => setForm((f) => ({ ...f, schedule: v }))}>
                  <SelectTrigger id="feed-schedule"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SCHEDULE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* URL + Detect */}
            <div className="space-y-1.5">
              <Label htmlFor="feed-url">URL</Label>
              <div className="flex gap-2">
                <Input id="feed-url" className="flex-1"
                  placeholder="https://example.com/feed.json"
                  value={form.url}
                  onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} />
                <Button variant="outline" size="sm" onClick={handleDetectFields}
                  disabled={isDetecting || !form.url.trim()}>
                  <Search className={`mr-1.5 h-3.5 w-3.5 ${isDetecting ? 'animate-spin' : ''}`} />
                  {isDetecting ? 'Detecting...' : 'Detect'}
                </Button>
              </div>
              {detectedFields.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Detected {detectedFields.length} fields — suggestions available in mapping inputs
                </p>
              )}
            </div>

            {/* Type + Format + Language */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="feed-type">Type</Label>
                <Select value={form.type} onValueChange={(v) => handleTypeChange(v as FeedType)}>
                  <SelectTrigger id="feed-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.keys(TYPE_LABELS) as FeedType[]).map((t) => (
                      <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="feed-format">Format</Label>
                <Select value={form.format} onValueChange={(v) => handleFormatChange(v as FeedFormat)}>
                  <SelectTrigger id="feed-format"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.keys(FORMAT_LABELS) as FeedFormat[]).map((fmt) => (
                      <SelectItem key={fmt} value={fmt}>{FORMAT_LABELS[fmt]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="feed-lang">Language</Label>
                <Select value={form.lang} onValueChange={(v) => setForm((f) => ({ ...f, lang: v }))}>
                  <SelectTrigger id="feed-lang"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANG_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ---- Field Mapping ---- */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold">Field Mapping</h3>
                <Badge variant="secondary" className="text-xs">
                  {TYPE_LABELS[form.type]}
                </Badge>
              </div>

              {/* Detected fields datalist */}
              {detectedFields.length > 0 && (
                <datalist id={datalistId}>
                  {detectedFields.map((f) => <option key={f} value={f} />)}
                </datalist>
              )}

              {/* Required fields */}
              <div className="grid grid-cols-3 gap-3">
                {requiredFields.map((field) => (
                  <div key={field.key} className="space-y-1">
                    <Label className="text-xs font-semibold">
                      {field.label} <span className="text-destructive">*</span>
                    </Label>
                    <Input className="h-8 text-sm"
                      placeholder={field.placeholder}
                      value={form.mapping[field.key] ?? ''}
                      onChange={(e) => setMappingField(field.key, e.target.value)}
                      list={detectedFields.length > 0 ? datalistId : undefined} />
                  </div>
                ))}
              </div>

              {/* Optional fields */}
              {optionalFields.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {optionalFields.map((field) => (
                    <div key={field.key} className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{field.label}</Label>
                      <Input className="h-8 text-sm"
                        placeholder={field.placeholder}
                        value={form.mapping[field.key] ?? ''}
                        onChange={(e) => setMappingField(field.key, e.target.value)}
                        list={detectedFields.length > 0 ? datalistId : undefined} />
                    </div>
                  ))}
                </div>
              )}

              {/* Structure fields (rootPath, itemPath) */}
              {showStructureFields && (
                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                  {STRUCTURE_FIELDS.map((field) => (
                    <div key={field.key} className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{field.label}</Label>
                      <Input className="h-8 text-sm"
                        placeholder={field.placeholder}
                        value={form.mapping[field.key] ?? ''}
                        onChange={(e) => setMappingField(field.key, e.target.value)} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ---- Pagination (JSON only) ---- */}
            {form.format === 'json' && (
              <div className="space-y-3 pt-2 border-t">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">Pagination</h3>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.pagination.enabled}
                      onChange={(e) => setForm((f) => ({
                        ...f,
                        pagination: { ...f.pagination, enabled: e.target.checked },
                      }))}
                      className="rounded border-gray-300"
                    />
                    <span className="text-xs text-muted-foreground">Enable paginated fetching</span>
                  </label>
                </div>

                {form.pagination.enabled && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Page Param</Label>
                      <Input className="h-8 text-sm" placeholder="pageNumber"
                        value={form.pagination.pageParam}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          pagination: { ...f.pagination, pageParam: e.target.value },
                        }))} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Page Size Param</Label>
                      <Input className="h-8 text-sm" placeholder="pageSize"
                        value={form.pagination.pageSizeParam}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          pagination: { ...f.pagination, pageSizeParam: e.target.value },
                        }))} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Page Size</Label>
                      <Input className="h-8 text-sm" type="number" placeholder="100"
                        value={form.pagination.pageSize}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          pagination: { ...f.pagination, pageSize: parseInt(e.target.value) || 100 },
                        }))} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Max Pages</Label>
                      <Input className="h-8 text-sm" type="number" placeholder="100"
                        value={form.pagination.maxPages}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          pagination: { ...f.pagination, maxPages: parseInt(e.target.value) || 100 },
                        }))} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Has Next Page Path</Label>
                      <Input className="h-8 text-sm" placeholder="hasNextPage"
                        value={form.pagination.hasNextPagePath}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          pagination: { ...f.pagination, hasNextPagePath: e.target.value },
                        }))} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Total Pages Path</Label>
                      <Input className="h-8 text-sm" placeholder="totalPages"
                        value={form.pagination.totalPagesPath}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          pagination: { ...f.pagination, totalPagesPath: e.target.value },
                        }))} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {formError && (
              <p className="text-sm text-destructive">{formError}</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setDialogOpen(false); setForm(EMPTY_FORM);
              setEditingFeedId(null); setFormError(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={creating}>
              {creating
                ? (dialogMode === 'edit' ? 'Saving...' : 'Creating...')
                : (dialogMode === 'edit' ? 'Save Changes' : 'Create Feed')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
